import { useReducer, useEffect, useState } from 'react'
import { useMutation } from '@apollo/client/react'
import classNames from 'classnames'
import { path } from 'ramda'
import Head from 'next/head'
import Link from 'next/link'

import LoginRedirect from '@/components/loginRedirect'
import Header from '@/components/newHeader/Header'
import Button from '@/components/button'
import Input from '@/components/input'
// utils
import { saveToken } from '@/utils/tokenStorage'
import { breederCoverdogs } from '@/utils/coverdogs'
import { setNoTrackUser } from '@/utils/notrackuser'

import styles from './breeder.module.scss'

import { CREATE_BREEDER } from '../../graphql'

const actions = {
  SET_FIRST_NAME: 'SET_FIRST_NAME',
  SET_LAST_NAME: 'SET_LAST_NAME',
  SET_EMAIL: 'SET_EMAIL',
  SET_PASSWORD: 'SET_PASSWORD',
  SET_PASSWORD_CONFIRM: 'SET_PASSWORD_CONFIRM',
  SET_TOS: 'SET_TOS',
  SET_ERROR: 'SET_ERROR',
}

const initialValue = {
  firstName: null,
  lastName: null,
  email: null,
  password: null,
  passwordConfirm: null,
  tos: false,
  error: null,
}

const reducer = (state, action) => {
  switch (action.type) {
    case actions.SET_FIRST_NAME:
      return {
        ...state,
        firstName: action.payload,
      }
    case actions.SET_LAST_NAME:
      return {
        ...state,
        lastName: action.payload,
      }
    case actions.SET_EMAIL:
      return {
        ...state,
        email: action.payload,
      }
    case actions.SET_PASSWORD:
      return {
        ...state,
        password: action.payload,
      }
    case actions.SET_PASSWORD_CONFIRM:
      return {
        ...state,
        passwordConfirm: action.payload,
      }
    case actions.SET_TOS:
      return {
        ...state,
        tos: action.payload,
      }
    case actions.SET_ERROR:
      return {
        ...state,
        error: action.payload,
      }
  }
  return state
}

export default function BreederLogin() {
  const [state, dispatch] = useReducer(reducer, initialValue)
  const [dogIndex, setDogIndex] = useState('breederCoverdog1')
  const [createBreeder, { data }] = useMutation(CREATE_BREEDER)
  const errorMessage =
    path(['createBreeder', 'errorMessage'], data) || state.error
  const JWT = path(['createBreeder', 'jwt'], data)
  if (JWT) {
    saveToken(JWT)
  }

  // sign up/sign in cover dogs //

  useEffect(() => {
    const i =
      breederCoverdogs[Math.floor(Math.random() * breederCoverdogs.length)]
    setDogIndex(i)
  }, [])

  const onFormSubmit = (e) => {
    e.preventDefault()
    if (state.password === state.passwordConfirm && state.tos) {
      dispatch({ type: actions.SET_ERROR, payload: null })
      createBreeder({
        variables: {
          breeder: {
            firstName: state.firstName,
            lastName: state.lastName,
            email: state.email,
            password: state.password,
          },
        },
      })
      setNoTrackUser('Breeder') // Breeder no tracking cookie
    } else {
      dispatch({
        type: actions.SET_ERROR,
        payload:
          state.password !== state.passwordConfirm
            ? 'Passwords do not match'
            : !state.tos
              ? 'Please review the Terms & Conditions to proceed'
              : null,
      })
    }
  }

  return (
    <div>
      <Head>
        {/*  Facebook tags  */}
        <meta name="og:title" content="Breeder Sign Up" key="title" />
        <meta name="og:type" content="website" key="type" />
        <meta
          name="og:url"
          content={`${process.env.NEXT_PUBLIC_ROOT_URL}/signup/breeder`}
          key="url"
        />
        <meta
          name="og:description"
          content="Apply today to join our community of responsible breeders. All breeders on Mawoo Pets are vetted for health, safety, and socialization standards — Start by signing up to submit an application!"
        />
        <meta name="og:locale" content="en_US" key="locale" />
        {/*  standard tags  */}
        <title>Breeder Sign Up</title>

        <meta
          name="description"
          content="Apply today to join our community of responsible breeders. All breeders on Mawoo Pets are vetted for health, safety, and socialization standards — Start by signing up to submit an application!"
        />
        <meta charSet="UTF-8" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1"
        />
        <meta
          name="robots"
          content={
            process.env.NEXT_PUBLIC_ROOT_URL === 'https://www.mawoopets.com'
              ? 'index, follow'
              : 'noindex, nofollow'
          }
        />
        <link
          rel="canonical"
          href={`${process.env.NEXT_PUBLIC_ROOT_URL}/signup/breeder`}
        />
      </Head>

      <main className={styles.main}>
        <LoginRedirect />
        <Header />
        <div className={styles.content}>
          <div className={styles.leftColumn}>
            <div className={styles.container}>
              <div className={styles.containerSpace} />
              <div className={styles.formHolder}>
                <h1 className={styles.title}>Create new account</h1>
                <div className={styles.subtitle}>
                  Create a new Mawoo Pets account
                </div>
                <form onSubmit={onFormSubmit}>
                  <div className={styles.row}>
                    <label htmlFor="firstname">
                      <Input
                        id="firstname"
                        placeholder="First Name"
                        type="text"
                        value={state.firstName}
                        onChange={(e) =>
                          dispatch({
                            type: actions.SET_FIRST_NAME,
                            payload: e.target.value,
                          })
                        }
                      />
                    </label>
                  </div>
                  <div className={styles.row}>
                    <label htmlFor="lastname">
                      <Input
                        id="lastname"
                        placeholder="Last Name"
                        type="text"
                        value={state.lastName}
                        onChange={(e) =>
                          dispatch({
                            type: actions.SET_LAST_NAME,
                            payload: e.target.value,
                          })
                        }
                      />
                    </label>
                  </div>
                  <div className={styles.row}>
                    <label htmlFor="email">
                      <Input
                        id="email"
                        placeholder="Enter Email"
                        value={state.email}
                        onChange={(e) =>
                          dispatch({
                            type: actions.SET_EMAIL,
                            payload: e.target.value,
                          })
                        }
                      />
                    </label>
                  </div>
                  <div className={styles.row}>
                    <label htmlFor="password">
                      <Input
                        id="password"
                        placeholder="Password"
                        type="password"
                        value={state.password}
                        onChange={(e) =>
                          dispatch({
                            type: actions.SET_PASSWORD,
                            payload: e.target.value,
                          })
                        }
                      />
                    </label>
                  </div>
                  <div className={styles.row}>
                    <label htmlFor="confirmpassword">
                      <Input
                        id="confirmpassword"
                        placeholder="Confirm Password"
                        type="password"
                        value={state.passwordConfirm}
                        onChange={(e) =>
                          dispatch({
                            type: actions.SET_PASSWORD_CONFIRM,
                            payload: e.target.value,
                          })
                        }
                      />
                    </label>
                  </div>
                  <div
                    className={styles.checkboxHolder}
                    onClick={() =>
                      dispatch({ type: actions.SET_TOS, payload: !state.tos })
                    }
                  >
                    <div
                      className={classNames({
                        [styles.checkbox]: true,
                        [styles.active]: state.tos,
                      })}
                    />
                    <div className={styles.checkboxText}>
                      I have read the{' '}
                      <Link href="/tos">Terms and Conditions</Link> and a{' '}
                      <Link href="/policy">Privacy Policy.</Link>
                    </div>
                  </div>
                  <div className={styles.lastrow}>
                    <Button
                      theme="new-pink-width"
                      title="Create Account"
                      type="submit"
                    />
                  </div>
                </form>
                <div className={styles.subtitle}>{errorMessage}</div>
              </div>
              <div className={styles.containerSpace}>
                <div className={styles.signin}>
                  <span className={styles.text}>
                    Already have an account ?{' '}
                  </span>
                  <Link href="/login/breeder" legacyBehavior>
                    <span className={styles.link}>Sign In</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
          <div
            className={styles.image}
            style={{
              backgroundImage: `url("/signup/coverdogs/breeder/${dogIndex}.png")`,
            }}
            role="img"
            aria-label={dogIndex}
          />
        </div>
      </main>
    </div>
  )
}
